import type { ApiEnvelope } from "./types/generated/ApiEnvelope";
import type { ErrorDetail } from "./types/generated/ErrorDetail";
import { WIRE_ACCEPT } from "./types/generated/wireContract";

export type {
  AuthRole,
  AuthUser,
  ChangePasswordRequest,
  ClientLoginRequest,
  CompleteRegistrationRequest,
  CsrfResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshRequest,
  ResetPasswordRequest,
  SessionResponse,
} from "./types/auth";
export { AuthRoleSchema } from "./types/auth";
export type {
  BulkStatusRequest,
  Company,
  CompanyBulkStatusResult,
  CompanyCreateRequest,
  CompanyDetail,
  CompanyExportResult,
  CompanyList,
  CompanyStatus,
  CompanyStatusChange,
  CompanyStatusRequest,
  CompanyUpdateRequest,
  CreateCompanyResponse,
  ExportRequest,
  ResendInviteResponse,
  ClientProfile,
} from "./types/company";
export type { AuditLog, AuditLogsResponse, LogsQuery, PaginationMeta } from "./types/log";
export type { Capabilities } from "./types/generated/Capabilities";
export type {
  PicBrowserBinding,
  PicAccess,
  BrowserConfirmationRequired,
  ConfirmBrowserRequest,
  ConfirmBrowserResult,
  PicBrowsersRevokeResult,
  PicReasonRequest,
  PicSuspendedResult,
} from "./types/pic";
export type {
  AccessAlertPage,
  AccessAlertView,
  BaselineRosterEntry,
  BulkReviewStatus,
  FdsAction,
  FdsCaseDetail,
  FdsCasePage,
  FdsCaseView,
  FdsDecisionRequest,
  FdsDecisionResult,
  FdsQueueView,
  FdsTrailLine,
  CreateBaselineRequest,
  FdsRecipientState,
  KycApproval,
  KycRevocation,
  ListCheckResult,
  PayrollBaseline,
  PayrollBaselineHistory,
  PayrollCycle,
  PendingBatchItem,
  RecordKycRequest,
  RevokeKycRequest,
} from "./types/fds";
export type {
  ImportRecipientsResult,
  PayrollCreateRequest,
  PayrollRun,
  PayrollRunList,
  PayrollStatus,
  PayrollStatusRequest,
  RecipientRecord,
  RecipientRecordList,
  RecipientStatus,
  RecipientStatusChange,
  RecipientStatusRequest,
} from "./types/payroll";
export type {
  BulkConfirmation,
  BulkQueueActionItemCounts,
  BulkQueueActionResult,
  BulkQueueItem,
  BulkQueuePage,
  BulkQueueQuery,
  BulkReportQuery,
  ReportFormat,
  BulkTransactionItem,
  BulkTransactionItemStatus,
  BulkTransactionRecord,
  BulkTransactionStatus,
  BulkTransferQuery,
  BulkTransferValidationItem,
  BulkTransferValidationSummary,
  Channel,
  FundingOutcome,
  PayInRequest,
  UploadedBulkTransfer,
} from "./types/transaction";
export {
  BACK_OFFICE_ID_FORMAT_MESSAGE,
  BACK_OFFICE_ID_PATTERN,
  canonicalizeBackOfficeId,
} from "./backOfficeId";
export {
  PASSWORD_MAX_LEN,
  PASSWORD_MIN_LEN,
} from "./types/generated/passwordPolicy";

export type { ApiEnvelope } from "./types/generated/ApiEnvelope";
export type { ApiMeta } from "./types/generated/ApiMeta";
export type { ErrorDetail } from "./types/generated/ErrorDetail";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiRequestOptions<TBody = unknown, _TResponse = unknown> = {
  /**
   * Path relative to API base (e.g., "/system").
   */
  path: string;
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Skip Authorization header (e.g., login, refresh).
   */
  auth?: boolean;
  /**
   * Allow automatic token refresh on 401. Defaults to true.
   */
  allowRefresh?: boolean;
};

export class ApiError extends Error {
  httpStatus: number;
  errorCode?: string;
  errorDetails?: ErrorDetail[];
  rawBody?: unknown;

  constructor(
    init: Pick<Error, "message"> &
      Pick<ApiError, "httpStatus" | "errorCode" | "errorDetails" | "rawBody">,
  ) {
    super(init.message);
    this.name = "ApiError";
    this.httpStatus = init.httpStatus;
    this.errorCode = init.errorCode;
    this.errorDetails = init.errorDetails;
    this.rawBody = init.rawBody;
  }
}

let refreshInFlight: Promise<boolean> | null = null;
let _sessionExpiredKey = "kkp_session_expired";
let _sessionStorageKey: string | null = null;
let _getBaseUrl: (() => string | undefined) | null = null;
let _csrfToken: string | null = null;

export type ApiClientConfig = {
  getBaseUrl: () => string | undefined;
  sessionExpiredKey: string;
  sessionStorageKey?: string;
};

export function configureClient(opts: ApiClientConfig) {
  _getBaseUrl = opts.getBaseUrl;
  _sessionExpiredKey = opts.sessionExpiredKey;
  _sessionStorageKey = opts.sessionStorageKey ?? null;
}

function safeGetLocalStorageItem(key: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorageItem(key: string, value: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemoveLocalStorageItem(key: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function getCsrfStorageKey(): string | null {
  return _sessionStorageKey ? `${_sessionStorageKey}:csrf` : null;
}

function getStoredCsrfToken(): string | null {
  const storageKey = getCsrfStorageKey();
  const persisted = storageKey ? safeGetLocalStorageItem(storageKey) : null;
  const trimmedPersisted = persisted?.trim();

  if (trimmedPersisted) {
    _csrfToken = trimmedPersisted;
    return trimmedPersisted;
  }

  return _csrfToken;
}

function storeCsrfToken(token: unknown): void {
  if (typeof token !== "string") {
    return;
  }

  const trimmed = token.trim();
  if (!trimmed) {
    return;
  }

  _csrfToken = trimmed;

  const storageKey = getCsrfStorageKey();
  if (storageKey) {
    safeSetLocalStorageItem(storageKey, trimmed);
  }
}

function clearStoredCsrfToken(): void {
  _csrfToken = null;

  const storageKey = getCsrfStorageKey();
  if (storageKey) {
    safeRemoveLocalStorageItem(storageKey);
  }
}

function captureCsrfToken(payload: unknown): void {
  if (!payload || typeof payload !== "object") {
    return;
  }

  const record = payload as Record<string, unknown>;

  if (typeof record.csrfToken === "string") {
    storeCsrfToken(record.csrfToken);
  }

  if (record.data && typeof record.data === "object") {
    captureCsrfToken(record.data);
  }
}

function requiresCsrf(method: HttpMethod): boolean {
  return !["GET", "HEAD"].includes(method);
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  const target = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === target);
}

function parseStoredTokens(): { access: string; refresh: string } | null {
  return { access: "cookie", refresh: "cookie" };
}

function hardLogout() {
  try {
    clearStoredCsrfToken();
    if (_sessionStorageKey) {
      window.localStorage.removeItem(_sessionStorageKey);
    }
    window.localStorage.setItem(_sessionExpiredKey, "1");
  } catch {
    // ignore
  }
  window.location.hash = "#/login";
}

function buildHeaders(
  method: HttpMethod,
  baseHeaders: Record<string, string> | undefined,
  body: unknown,
  token: string | null,
  auth: boolean,
): Record<string, string> {
  const headers: Record<string, string> = { ...(baseHeaders ?? {}) };
  if (auth && token && token !== "cookie") {
    headers.Authorization = `Bearer ${token}`;
  }
  const hasBody = body !== undefined && body !== null;
  const isJsonBody =
    hasBody && typeof body === "object" && !(body instanceof FormData);
  if (hasBody && isJsonBody && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (!hasHeader(headers, "accept")) {
    headers.Accept = WIRE_ACCEPT;
  }

  // Double-submit CSRF: attach the stored token to any mutating request that has
  // one, independent of the bearer `auth` flag. Logout is issued as an auth:false
  // call (it rides the session cookies, not a bearer token), but the BFFE still
  // enforces the double-submit check on it (csrf.rs keeps logout OUT of the skip
  // allowlist), so it must echo the token too — otherwise logout is 403'd and
  // never revokes the session server-side.
  if (requiresCsrf(method) && !hasHeader(headers, "x-csrf-token")) {
    const csrfToken = getStoredCsrfToken();
    if (csrfToken) {
      headers["x-csrf-token"] = csrfToken;
    }
  }

  return headers;
}

const WIRE_RELOAD_KEY = "kkp_wire_reload";

// A BFFE that no longer serves this bundle's wire contract answers 406; reload
// once per session per contract, so a cached bundle cannot loop.
function reloadOnRetiredContract(res: Response): void {
  if (res.status !== 406) return;
  try {
    if (window.sessionStorage.getItem(WIRE_RELOAD_KEY) === WIRE_ACCEPT) return;
    window.sessionStorage.setItem(WIRE_RELOAD_KEY, WIRE_ACCEPT);
  } catch {
    return;
  }
  window.location.reload();
}

async function doFetch<T>(
  url: string,
  init: RequestInit,
  expectsAuth: boolean,
): Promise<T> {
  const res = await fetch(url, init);
  reloadOnRetiredContract(res);

  // 204 success (no body)
  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = res.headers.get("content-type") ?? "";

  // CSV success case
  if (contentType.startsWith("text/csv")) {
    const csv = await res.text();
    return csv as unknown as T;
  }

  // PDF success case
  if (contentType.startsWith("application/pdf")) {
    const blob = await res.blob();
    return blob as unknown as T;
  }

  // Parse JSON envelope or raw text fallback
  let parsed: unknown = null;
  if (contentType.includes("application/json")) {
    parsed = await res.json().catch(() => null);
  } else {
    const text = await res.text().catch(() => null);
    parsed = text;
  }

  if (
    contentType.includes("application/json") &&
    parsed &&
    typeof parsed === "object" &&
    "success" in parsed
  ) {
    const envelope = parsed as ApiEnvelope<T>;
    if (envelope.success) {
      captureCsrfToken(envelope.data);
      return (envelope.data ?? undefined) as T;
    }

    if (expectsAuth && res.status === 401) {
      throw new ApiError({
        message: envelope.message || "Auth expired",
        httpStatus: res.status,
        errorCode: envelope.error?.code,
        errorDetails: envelope.error?.details,
        rawBody: envelope,
      });
    }

    throw new ApiError({
      message: envelope.message || "Request failed",
      httpStatus: res.status,
      errorCode: envelope.error?.code,
      errorDetails: envelope.error?.details,
      rawBody: envelope,
    });
  }

  captureCsrfToken(parsed);

  if (!res.ok) {
    throw new ApiError({
      message: `Request failed: ${res.status} ${res.statusText}`.trim(),
      httpStatus: res.status,
      rawBody: parsed,
    });
  }

  return parsed as T;
}

async function doFetchEnvelope<T>(
  url: string,
  init: RequestInit,
  expectsAuth: boolean,
): Promise<ApiEnvelope<T>> {
  const res = await fetch(url, init);
  reloadOnRetiredContract(res);

  if (res.status === 204) {
    return { success: true, data: undefined } as ApiEnvelope<T>;
  }

  const contentType = res.headers.get("content-type") ?? "";
  let parsed: unknown = null;

  if (contentType.includes("application/json")) {
    parsed = await res.json().catch(() => null);
  } else {
    const text = await res.text().catch(() => null);
    parsed = text;
  }

  if (
    contentType.includes("application/json") &&
    parsed &&
    typeof parsed === "object" &&
    "success" in parsed
  ) {
    const envelope = parsed as ApiEnvelope<T>;
    if (envelope.success) {
      captureCsrfToken(envelope.data);
      return envelope;
    }

    if (expectsAuth && res.status === 401) {
      throw new ApiError({
        message: envelope.message || "Auth expired",
        httpStatus: res.status,
        errorCode: envelope.error?.code,
        errorDetails: envelope.error?.details,
        rawBody: envelope,
      });
    }

    throw new ApiError({
      message: envelope.message || "Request failed",
      httpStatus: res.status,
      errorCode: envelope.error?.code,
      errorDetails: envelope.error?.details,
      rawBody: envelope,
    });
  }

  captureCsrfToken(parsed);

  if (!res.ok) {
    throw new ApiError({
      message: `Request failed: ${res.status} ${res.statusText}`.trim(),
      httpStatus: res.status,
      rawBody: parsed,
    });
  }

  return { success: true, data: parsed } as ApiEnvelope<T>;
}

function getApiBaseUrl(): string | undefined {
  return _getBaseUrl?.();
}

async function refreshTokens(): Promise<boolean> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) return false;

  // The BFFE does not exempt /auth/refresh from the double-submit CSRF
  // middleware. The first call after login has the csrf_token cookie +
  // stored CSRF token (set by the login response), so attach it here. The
  // "no token yet" case is left as an empty header and the BFFE rejects it
  // with 403, which the api-client treats as a refresh failure and forces
  // re-login — preferable to silently bypassing CSRF.
  const headers: Record<string, string> = {
    Accept: WIRE_ACCEPT,
    "Content-Type": "application/json",
  };
  const csrfToken = getStoredCsrfToken();
  if (csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }

  const res = await fetch(`${baseUrl}/auth/refresh`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({}),
  });
  reloadOnRetiredContract(res);

  const contentType = res.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    const parsed = await res.json().catch(() => null);
    captureCsrfToken(parsed);
  }

  return res.ok && Boolean(getStoredCsrfToken());
}

async function ensureCsrfToken(
  method: HttpMethod,
  auth: boolean,
): Promise<void> {
  if (!auth || !requiresCsrf(method) || getStoredCsrfToken()) {
    return;
  }

  if (!refreshInFlight) {
    refreshInFlight = refreshTokens();
  }

  const refreshSuccess = await refreshInFlight.catch(() => false);
  refreshInFlight = null;

  if (!refreshSuccess) {
    clearStoredCsrfToken();
  }
}

/**
 * Shared request scaffold: base-URL guard, CSRF priming, request building, and
 * the reactive 401-refresh retry. The only thing that differs between the raw
 * (`apiRequest`) and envelope (`apiRequestEnvelope`) helpers is which fetch
 * routine unwraps the response, so that is injected as `doRequest`.
 */
async function performRequest<TResponse, TBody, TResult>(
  options: ApiRequestOptions<TBody, TResponse>,
  doRequest: (
    url: string,
    init: RequestInit,
    expectsAuth: boolean,
  ) => Promise<TResult>,
): Promise<TResult> {
  const {
    path,
    method = "GET",
    body,
    headers,
    signal,
    auth = true,
    allowRefresh = true,
  } = options;

  const baseUrl = getApiBaseUrl();

  if (!baseUrl) {
    throw new ApiError({
      message: "API base URL is not configured",
      httpStatus: 500,
    });
  }

  await ensureCsrfToken(method, auth);

  const attempt = async (retried: boolean): Promise<TResult> => {
    const tokens = auth ? parseStoredTokens() : null;
    const url = `${baseUrl}${path}`;
    const init: RequestInit = {
      method,
      signal,
      credentials: "include",
      headers: buildHeaders(
        method,
        headers,
        body,
        auth ? (tokens?.access ?? null) : null,
        auth,
      ),
      body:
        body === undefined || body === null
          ? undefined
          : typeof body === "object" && !(body instanceof FormData)
            ? JSON.stringify(body)
            : (body as BodyInit),
    };

    try {
      const response = await doRequest(url, init, auth);

      if (path.endsWith("/auth/logout")) {
        clearStoredCsrfToken();
      }

      return response;
    } catch (err) {
      const apiErr = err as ApiError;
      const needsRefresh = auth && allowRefresh && apiErr.httpStatus === 401;

      if (needsRefresh) {
        if (retried) {
          hardLogout();
          throw apiErr;
        }

        if (!refreshInFlight) {
          refreshInFlight = refreshTokens();
        }

        const refreshSuccess = await refreshInFlight.catch(() => false);
        refreshInFlight = null;

        if (!refreshSuccess) {
          hardLogout();
          throw apiErr;
        }

        return attempt(true);
      }

      throw apiErr;
    }
  };

  return attempt(false);
}

/**
 * API request helper that returns the unwrapped data, with reactive refresh
 * (401 only).
 */
export async function apiRequest<TResponse = unknown, TBody = unknown>(
  options: ApiRequestOptions<TBody, TResponse>,
): Promise<TResponse> {
  return performRequest(options, (url, init, expectsAuth) =>
    doFetch<TResponse>(url, init, expectsAuth),
  );
}

/**
 * Like {@link apiRequest} but resolves to the full {@link ApiEnvelope} (data +
 * pagination/meta) instead of just the unwrapped data.
 */
export async function apiRequestEnvelope<TResponse = unknown, TBody = unknown>(
  options: ApiRequestOptions<TBody, TResponse>,
): Promise<ApiEnvelope<TResponse>> {
  return performRequest(options, (url, init, expectsAuth) =>
    doFetchEnvelope<TResponse>(url, init, expectsAuth),
  );
}
